import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Heart, Edit, Save, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityDashboard = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({
    founder_message: '',
    community_announcements: ''
  });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.email === 'superadmin@pilgrimhealth.com';

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/community/announcements`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnnouncements(response.data);
      setEditedData({
        founder_message: response.data.founder_message || '',
        community_announcements: response.data.community_announcements || ''
      });
    } catch (error) {
      // console.error('Error fetching announcements:', error);
      toast.error('Failed to load community announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/community/announcements`,
        editedData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Announcements updated successfully!');
      setEditMode(false);
      fetchAnnouncements();
    } catch (error) {
      // console.error('Error updating announcements:', error);
      toast.error('Failed to update announcements');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData({
      founder_message: announcements?.founder_message || '',
      community_announcements: announcements?.community_announcements || ''
    });
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6 border-t-4 border-cyan-600">
          <div className="flex items-center justify-center mb-4">
            <Users size={40} className="text-cyan-600 mr-3" />
            <h1 className="text-4xl font-bold text-slate-900">Community Dashboard</h1>
          </div>
          <div className="flex items-center justify-center">
            <Heart size={20} className="text-red-500 mr-2" />
            <p className="text-xl text-slate-700">Let's support one another in our health care journey!</p>
            <Heart size={20} className="text-red-500 ml-2" />
          </div>
        </div>

        {/* Message from Dr. TJ */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-cyan-200">
            <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              💙 Message from Dr. TJ, Founder of Pilgrim Health
            </h2>
            {isSuperAdmin && !editMode && (
              <Button
                onClick={() => setEditMode(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white text-sm px-4 py-2 rounded-lg"
              >
                <Edit size={16} className="mr-1" />
                Edit
              </Button>
            )}
          </div>

          {editMode && isSuperAdmin ? (
            <div className="space-y-4">
              <Textarea
                value={editedData.founder_message}
                onChange={(e) => setEditedData({ ...editedData, founder_message: e.target.value })}
                placeholder="Write a message from the founder..."
                className="w-full min-h-[200px] text-base"
              />
            </div>
          ) : (
            <div className="prose max-w-none">
              {announcements?.founder_message ? (
                <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {announcements.founder_message}
                </p>
              ) : (
                <p className="text-slate-400 italic">No message yet. Check back soon!</p>
              )}
            </div>
          )}
        </div>

        {/* Community Announcements */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-purple-200">
            <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              📢 Community Announcements
            </h2>
          </div>

          {editMode && isSuperAdmin ? (
            <div className="space-y-4">
              <Textarea
                value={editedData.community_announcements}
                onChange={(e) => setEditedData({ ...editedData, community_announcements: e.target.value })}
                placeholder="Write community announcements..."
                className="w-full min-h-[200px] text-base"
              />
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <Button
                  onClick={handleCancel}
                  disabled={saving}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-2 rounded-lg"
                >
                  <X size={16} className="mr-1" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg"
                >
                  <Save size={16} className="mr-1" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="prose max-w-none">
              {announcements?.community_announcements ? (
                <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {announcements.community_announcements}
                </p>
              ) : (
                <p className="text-slate-400 italic">No announcements yet. Check back soon!</p>
              )}
            </div>
          )}
        </div>

        {/* Last Updated Info */}
        {announcements?.updated_at && (
          <div className="text-center text-sm text-slate-500">
            Last updated: {new Date(announcements.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityDashboard;
